/**
 * Yönetim tanım ekranlarının ortak kabuk parçaları.
 *
 * Burada YENİ bir tasarım dili üretilmez; @kantin/ui bileşenleri sarmalanır.
 * Amaç tek şey: her liste/kart için YÜKLENİYOR, BOŞ ve HATA durumlarının
 * üçünün de yazılmış olmasını bir alışkanlığa değil, bir bileşene bağlamak.
 */
import type { ReactNode, TextareaHTMLAttributes } from "react";
import { forwardRef, useEffect, useId, useRef, useState } from "react";
import {
  Alan,
  BosDurum,
  Buton,
  IkonButon,
  IkonKopyala,
  IkonTik,
  Iskelet,
  Uyari,
} from "@kantin/ui";
import { ApiHatasi } from "./api.js";

// ---------------------------------------------------------------------------
// Metin ve biçimlendirme
// ---------------------------------------------------------------------------

export function hataMetni(e: unknown): string {
  if (e instanceof ApiHatasi) return e.message;
  if (e instanceof Error) return e.message;
  return "Beklenmeyen bir hata oluştu.";
}

/** Okul saat diliminde değil, tarayıcının yerelinde — yalnızca SUNUM için. */
export function bugun(): string {
  return new Date().toLocaleDateString("en-CA");
}

export function ayBasi(): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString("en-CA");
}

export function tarihGoster(deger: string | Date | null | undefined): string {
  if (!deger) return "—";
  const d = typeof deger === "string" ? new Date(deger) : deger;
  if (Number.isNaN(d.getTime())) return String(deger);
  return d.toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function tarihSaatGoster(
  deger: string | Date | null | undefined,
): string {
  if (!deger) return "—";
  const d = typeof deger === "string" ? new Date(deger) : deger;
  if (Number.isNaN(d.getTime())) return String(deger);
  return d.toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * "3 sa 20 dk" — yaşlanma göstergesi (KK-1213).
 * Girdi sunucudan gelen saat cinsinden ondalıklı string'dir ("3.34").
 * Para değeri OLMADIĞI için burada sayıya çevirmek serbesttir; yine de
 * Number() değil Number.parseFloat değil, doğrudan ayrıştırma kullanılır.
 */
export function saatMetni(saat: string | null | undefined): string {
  if (!saat) return "—";
  const [tamKisim = "0", kesirKisim = ""] = saat.split(".");
  const tam = Number.parseInt(tamKisim, 10);
  if (Number.isNaN(tam)) return "—";
  const dakika = Number.parseInt(`${kesirKisim}00`.slice(0, 2), 10);
  const dk = Number.isNaN(dakika) ? 0 : Math.round((dakika * 60) / 100);
  if (tam === 0) return `${dk} dk`;
  return dk === 0 ? `${tam} sa` : `${tam} sa ${dk} dk`;
}

/** Para girişi doğrulaması — değer STRING kalır, sayıya çevrilmez (ADR-03). */
export function paraGecerliMi(deger: string): boolean {
  return /^\d{1,10}([.,]\d{1,2})?$/.test(deger.trim());
}

/** Kullanıcı virgül yazar, sunucu nokta bekler. Değer yine string'dir. */
export function paraNormalize(deger: string): string {
  return deger.trim().replace(",", ".");
}

export function miktarGecerliMi(deger: string): boolean {
  return /^\d{1,10}([.,]\d{1,3})?$/.test(deger.trim());
}

export function miktarNormalize(deger: string): string {
  return deger.trim().replace(",", ".");
}

/**
 * Miktarı BİR artırır — barkod okutma "sayarak" ilerler, her okutma bir adet.
 *
 * ADR-03 refleksi miktarda da korunur: değer STRING kalır ve float aritmetiği
 * YAPILMAZ. `0.1 + 1` kayan noktada `1.1000000000000001` üretir; bunun stok
 * miktarına sızması, sayım farkı olarak geri döner. Bu yüzden yalnızca tam
 * sayı kısmı BigInt ile artırılır, kesir ve ayraç olduğu gibi korunur
 * ("0,5" → "1,5", "12.250" → "13.250").
 *
 * Boş değer "1" olur. Bozuk değer SESSİZCE düzeltilmez: `null` döner ve
 * çağıran kullanıcıya satırın düzeltilmesi gerektiğini söyler.
 */
export function miktarBirArtir(deger: string): string | null {
  const d = deger.trim();
  if (d === "") return "1";
  if (!miktarGecerliMi(d)) return null;
  const ayrac = d.includes(",") ? "," : ".";
  const [tam, kesir] = d.split(/[.,]/);
  const artan = (BigInt(tam || "0") + 1n).toString();
  return kesir === undefined ? artan : `${artan}${ayrac}${kesir}`;
}

/**
 * Miktarın okunabilir hâli: "38.000" -> "38", "1.500" -> "1,5".
 * Yalnızca STRING üzerinde çalışır; sayıya çevirme yoktur (ADR-03 disiplini
 * para için zorunlu, miktarda da aynı refleks korunur).
 */
export function miktarGoster(deger: string | null | undefined): string {
  if (deger === null || deger === undefined || deger === "") return "—";
  const eksi = deger.startsWith("-");
  const mutlak = eksi ? deger.slice(1) : deger;
  const [tam = "0", kesir = ""] = mutlak.split(".");
  const kirpilmis = kesir.replace(/0+$/, "");
  const govde = kirpilmis ? `${tam},${kirpilmis}` : tam;
  return eksi ? `-${govde}` : govde;
}

// ---------------------------------------------------------------------------
// Sayfa kabuğu
// ---------------------------------------------------------------------------

export function SayfaBasligi({
  baslik,
  aciklama,
  eylem,
}: {
  baslik: string;
  aciklama?: ReactNode;
  eylem?: ReactNode;
}) {
  return (
    <header className="yonetim-sayfa-basligi flex flex-wrap items-center justify-between gap-4">
      <div className="min-w-0 max-w-icerik">
        <h1 className="m-0 text-2xl">{baslik}</h1>
        {aciklama && (
          <p className="m-0 mt-1 text-sm text-murekkep-2">{aciklama}</p>
        )}
      </div>
      {eylem && (
        <div className="flex flex-wrap items-center gap-2">{eylem}</div>
      )}
    </header>
  );
}

export function Sayfa({ children }: { children: ReactNode }) {
  return (
    <div className="yonetim-sayfa flex flex-col gap-5 p-4 md:p-8">
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Yükleniyor / hata / boş — üçü de HER liste için zorunlu
// ---------------------------------------------------------------------------

export function SorguDurumu({
  yukleniyor,
  hata,
  bos,
  bosBaslik = "Kayıt yok",
  bosAciklama,
  bosEylem,
  iskeletSatir = 5,
  yenile,
  children,
}: {
  yukleniyor: boolean;
  hata: unknown;
  bos: boolean;
  bosBaslik?: string;
  bosAciklama?: string;
  bosEylem?: ReactNode;
  iskeletSatir?: number;
  yenile?: () => void;
  children: ReactNode;
}) {
  if (yukleniyor) {
    return (
      <div className="p-4">
        <Iskelet satir={iskeletSatir} />
      </div>
    );
  }
  if (hata) {
    return (
      <div className="p-4">
        <Uyari tur="hata" baslik="Veriler yüklenemedi">
          <p className="m-0">{hataMetni(hata)}</p>
          {yenile && (
            <p className="m-0 mt-2">
              <Buton tur="ikincil" onClick={yenile}>
                Tekrar dene
              </Buton>
            </p>
          )}
        </Uyari>
      </div>
    );
  }
  if (bos) {
    return (
      <BosDurum
        baslik={bosBaslik}
        {...(bosAciklama === undefined ? {} : { aciklama: bosAciklama })}
        {...(bosEylem === undefined ? {} : { eylem: bosEylem })}
      />
    );
  }
  return <>{children}</>;
}

/** Kaydetme/silme sonucunu duyuran canlı bölge. */
export function IslemDurumu({
  hata,
  basari,
}: {
  hata?: unknown;
  basari?: string | null;
}) {
  if (hata) {
    return (
      <Uyari tur="hata" baslik="İşlem tamamlanamadı">
        {hataMetni(hata)}
      </Uyari>
    );
  }
  if (basari) {
    return <Uyari tur="onay">{basari}</Uyari>;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Form parçaları — @kantin/ui'da olmayanlar
// ---------------------------------------------------------------------------

export interface MetinAlaniOzellikleri extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  etiket: string;
  ipucu?: string;
  hata?: string;
}

export const MetinAlani = forwardRef<
  HTMLTextAreaElement,
  MetinAlaniOzellikleri
>(function MetinAlani(
  { etiket, ipucu, hata, className = "", id, ...kalan },
  ref,
) {
  const kendiId = useId();
  const alanId = id ?? kendiId;
  const ipucuId = `${alanId}-ipucu`;
  const hataId = `${alanId}-hata`;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={alanId} className="text-sm font-satir text-murekkep">
        {etiket}
      </label>
      <textarea
        ref={ref}
        id={alanId}
        aria-invalid={hata ? true : undefined}
        aria-describedby={
          [ipucu ? ipucuId : null, hata ? hataId : null]
            .filter(Boolean)
            .join(" ") || undefined
        }
        className={`min-h-16 rounded-md border bg-girdi px-2.5 py-1.5 font-govde text-md text-murekkep transition-colors duration-defter ease-orta placeholder:text-murekkep-3 ${
          hata ? "border-hata" : "border-cetvel hover:border-cetvel-koyu"
        } ${className}`}
        {...kalan}
      />
      {ipucu && !hata && (
        <p id={ipucuId} className="m-0 text-xs text-murekkep-3">
          {ipucu}
        </p>
      )}
      {hata && (
        <p id={hataId} className="m-0 text-xs text-hata" role="alert">
          {hata}
        </p>
      )}
    </div>
  );
});

export function OnayKutusu({
  etiket,
  aciklama,
  isaretli,
  degistir,
  devreDisi = false,
}: {
  etiket: ReactNode;
  aciklama?: ReactNode;
  isaretli: boolean;
  degistir: (yeni: boolean) => void;
  devreDisi?: boolean;
}) {
  const id = useId();
  const aciklamaId = `${id}-aciklama`;
  return (
    <div className="flex items-start gap-2">
      <input
        id={id}
        type="checkbox"
        checked={isaretli}
        disabled={devreDisi}
        onChange={(e) => degistir(e.target.checked)}
        aria-describedby={aciklama ? aciklamaId : undefined}
        className="mt-0.5 h-4 w-4 shrink-0 accent-simit"
      />
      <div>
        <label htmlFor={id} className="text-sm text-murekkep">
          {etiket}
        </label>
        {aciklama && (
          <p id={aciklamaId} className="m-0 text-xs text-murekkep-3">
            {aciklama}
          </p>
        )}
      </div>
    </div>
  );
}

/** Arama kutusu — yazarken değil, 300 ms duraklayınca sorgular. */
export function AramaKutusu({
  etiket = "Ara",
  ipucu,
  deger,
  degistir,
}: {
  etiket?: string;
  ipucu?: string;
  deger: string;
  degistir: (yeni: string) => void;
}) {
  const id = useId();
  const [yerel, setYerel] = useState(deger);

  useEffect(() => setYerel(deger), [deger]);
  useEffect(() => {
    if (yerel === deger) return;
    const zamanlayici = window.setTimeout(() => degistir(yerel), 300);
    return () => window.clearTimeout(zamanlayici);
    // `degistir` her renderda yeni referans olabilir; bağımlılığa alınmaz —
    // aksi hâlde her renderda zamanlayıcı sıfırlanır ve debounce hiç tetiklenmez.
    // `deger` de bilerek dışarıdadır: yukarıdaki effect onu zaten `yerel`e yansıtır.
  }, [yerel]);

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-satir text-murekkep">
        {etiket}
      </label>
      <input
        id={id}
        type="search"
        value={yerel}
        onChange={(e) => setYerel(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== "Enter") return;
          e.preventDefault();
          degistir(yerel.trim());
        }}
        placeholder={ipucu}
        className="min-h-kontrol rounded-md border border-cetvel bg-girdi px-2.5 font-govde text-md text-murekkep transition-colors duration-defter ease-orta hover:border-cetvel-koyu focus:border-simit"
      />
    </div>
  );
}

export interface BarkodSonucu {
  tur: "onay" | "hata";
  mesaj: string;
}

/**
 * Barkod okutma alanı — mal kabul, transfer ve sayım ekranlarının ortak girişi.
 *
 * NEDEN AYRI BİR BİLEŞEN:
 *
 * 1) ENTER TUZAĞI. USB HID "wedge" barkod okuyucu klavye taklidi yapar:
 *    rakamları yazar ve sonuna ENTER basar. Bu alan bir `<form>` içinde
 *    durduğu için o Enter, formun VARSAYILAN gönderimini tetiklerdi — tek bir
 *    ürün okutmak, yarım kalmış fişi ya da sayımı sunucuya yollardı. Enter
 *    burada durdurulur (preventDefault) ve yalnızca okutma işlenir. Aynı
 *    refleks kasada da vardır (apps/kasa · SatisEkrani.tsx · barkodGonder).
 *
 * 2) SESSİZ BAŞARISIZLIK OLMAZ. Okutulan barkod tanımlı değilse kasiyer bunu
 *    DUYMALIDIR; yoksa "okuttum, girdi sandım" ile kalem hiç envantere
 *    girmez. Her okutmanın sonucu canlı bölgede duyurulur.
 *
 * 3) ODAK. Okuyucu, odak neredeyse oraya yazar. Ekran miktar girişine
 *    yönlendirmezse odak alanda kalır. F2 ile her yerden buraya çağrılır;
 *    kullanıcı elini okuyucudan çekip fare aramak zorunda kalmaz.
 *
 * Kararı bileşen VERMEZ: okunan değeri `onOkut`a verir, ekranın döndürdüğü
 * sonucu gösterir. Barkodun hangi satıra ne yaptığı ekranın bilgisidir.
 */
export function BarkodAlani({
  etiket = "Barkod okut",
  ipucu = "Okuyucuyu okutun veya barkodu yazıp Enter'a basın. Kısayol: F2.",
  pasif = false,
  pasifMetni,
  onOkut,
}: {
  etiket?: string;
  ipucu?: string;
  pasif?: boolean;
  pasifMetni?: string;
  onOkut: (barkod: string) => BarkodSonucu;
}) {
  const alanRef = useRef<HTMLInputElement | null>(null);
  const [taslak, setTaslak] = useState("");
  const [sonuc, setSonuc] = useState<BarkodSonucu | null>(null);

  useEffect(() => {
    if (pasif) return;
    const tus = (e: KeyboardEvent) => {
      if (e.key !== "F2") return;
      e.preventDefault();
      alanRef.current?.focus();
      alanRef.current?.select();
    };
    window.addEventListener("keydown", tus);
    return () => window.removeEventListener("keydown", tus);
  }, [pasif]);

  const okut = () => {
    if (pasif) return;
    const deger = taslak.trim();
    if (deger === "") return;
    // Ekran onOkut içinde miktar alanına geçebilir; sonrasında odağı geri alma.
    alanRef.current?.focus();
    setSonuc(onOkut(deger));
    // Alan HER okutmada temizlenir: okuyucu bir sonrakini bunun üstüne yazar.
    setTaslak("");
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Alan
            ref={alanRef}
            etiket={etiket}
            value={taslak}
            onChange={(e) => {
              setTaslak(e.target.value);
              setSonuc(null);
            }}
            onKeyDown={(e) => {
              if (e.key !== "Enter") return;
              e.preventDefault();
              okut();
            }}
            disabled={pasif}
            autoComplete="off"
            spellCheck={false}
            inputMode="text"
            maxLength={64}
            ipucu={pasif && pasifMetni ? pasifMetni : ipucu}
          />
        </div>
        <Buton type="button" tur="ikincil" onClick={okut} disabled={pasif}>
          Ekle
        </Buton>
      </div>
      <p
        className={`m-0 min-h-[1rem] text-xs ${
          sonuc?.tur === "hata" ? "text-hata" : "text-murekkep-2"
        }`}
        role={sonuc?.tur === "hata" ? "alert" : "status"}
      >
        {sonuc?.mesaj ?? ""}
      </p>
    </div>
  );
}

export function Sayfalayici({
  sayfa,
  boyut,
  toplam,
  degistir,
}: {
  sayfa: number;
  boyut: number;
  toplam: number;
  degistir: (yeniSayfa: number) => void;
}) {
  const sonSayfa = Math.max(1, Math.ceil(toplam / boyut));
  if (toplam === 0) return null;
  const ilk = (sayfa - 1) * boyut + 1;
  const son = Math.min(sayfa * boyut, toplam);
  return (
    <nav
      className="flex min-h-satir flex-wrap items-center justify-between gap-3 border-t border-cetvel-silik px-3 py-2 text-sm text-murekkep-2"
      aria-label="Sayfalama"
    >
      <p className="m-0" role="status">
        <span className="rakam font-mono">{ilk}</span>–
        <span className="rakam font-mono">{son}</span> / toplam{" "}
        <span className="rakam font-mono">{toplam}</span> kayıt
      </p>
      <div className="flex items-center gap-2">
        <Buton
          tur="ikincil"
          disabled={sayfa <= 1}
          onClick={() => degistir(sayfa - 1)}
        >
          Önceki
        </Buton>
        <span className="rakam font-mono text-murekkep-3">
          {sayfa} / {sonSayfa}
        </span>
        <Buton
          tur="ikincil"
          disabled={sayfa >= sonSayfa}
          onClick={() => degistir(sayfa + 1)}
        >
          Sonraki
        </Buton>
      </div>
    </nav>
  );
}

/**
 * Geri alınamayan işlemler için AÇIK onay. Tek dokunuşla bloke etmek,
 * imha etmek veya pasife almak sahada geri dönüşü olmayan hatalar üretir.
 */
export function TehlikeliOnay({
  soru,
  aciklama,
  onayMetni = "Evet, uygula",
  vazgecMetni = "Vazgeç",
  onayla,
  vazgec,
  yukleniyor = false,
  cocuk,
}: {
  soru: string;
  aciklama?: ReactNode;
  onayMetni?: string;
  vazgecMetni?: string;
  onayla: () => void;
  vazgec: () => void;
  yukleniyor?: boolean;
  cocuk?: ReactNode;
}) {
  const id = useId();
  // Bilerek `alertdialog` DEĞİL: bu blok kipsel (modal) değildir ve odağı
  // yakalamaz. Yanlış rol, ekran okuyucuya olmayan bir davranış vaat eder.
  // Soru bir canlı bölge olarak duyurulur, blok ise adlandırılmış bir gruptur.
  return (
    <div
      role="group"
      aria-labelledby={id}
      className="rounded-lg border border-hata-cizgi bg-hata-yumusak p-4"
    >
      <p id={id} role="alert" className="m-0 font-satir text-murekkep">
        {soru}
      </p>
      {aciklama && (
        <div className="mt-1 text-sm text-murekkep-2">{aciklama}</div>
      )}
      {cocuk && <div className="mt-3">{cocuk}</div>}
      <div className="mt-3 flex flex-wrap gap-2">
        <Buton tur="tehlike" onClick={onayla} yukleniyor={yukleniyor}>
          {onayMetni}
        </Buton>
        <Buton tur="ikincil" onClick={vazgec} disabled={yukleniyor}>
          {vazgecMetni}
        </Buton>
      </div>
    </div>
  );
}

/**
 * Bölüm seçici.
 *
 * `role="tablist"/"tab"` BİLEREK kullanılmaz: tam ARIA sekme deseni
 * aria-controls + tabpanel + ok tuşlarıyla gezinme ister; yarım uygulanmış bir
 * desen, hiç uygulanmamış olmasından daha yanıltıcı olur. Bunun yerine
 * `aria-pressed` taşıyan gerçek düğmeler kullanılır — @kantin/ui'daki Grafik
 * kabuğunun "tablo olarak göster" düğmesiyle aynı yaklaşım.
 */
export function Sekmeler<T extends string>({
  secili,
  sec,
  sekmeler,
  etiket,
}: {
  secili: T;
  sec: (yeni: T) => void;
  sekmeler: { deger: T; metin: string; sayi?: number }[];
  etiket: string;
}) {
  return (
    <nav aria-label={etiket} className="flex flex-wrap gap-1">
      {sekmeler.map((s) => (
        <button
          key={s.deger}
          type="button"
          aria-pressed={s.deger === secili}
          aria-current={s.deger === secili ? "true" : undefined}
          onClick={() => sec(s.deger)}
          className={`inline-flex min-h-kontrol items-center gap-1.5 rounded-tam px-3 text-sm transition-colors duration-defter ease-orta ${
            s.deger === secili
              ? "bg-simit-yumusak font-satir text-simit-metin"
              : "text-murekkep-2 hover:bg-kagit-2 hover:text-murekkep"
          }`}
        >
          {s.metin}
          {s.sayi !== undefined && (
            <span className="rakam font-mono text-xs text-murekkep-3">
              {s.sayi}
            </span>
          )}
        </button>
      ))}
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Tek seferlik sır — geçici parola / PIN / kurtarma kodu
// ---------------------------------------------------------------------------

/**
 * Bir kez gösterilen sırrı tek tıkla panoya alır. Değer büyük ve seçilebilir
 * kalır; pano izni reddedilirse (güvensiz bağlam, tarayıcı ayarı) kullanıcı
 * elle seçmeye yönlendirilir. Platformdaki kurulum kodu deseniyle aynı.
 */
export function SirKopyala({
  deger,
  etiket,
}: {
  deger: string;
  etiket: string;
}) {
  const [kopyalandi, setKopyalandi] = useState(false);
  const [panoHatasi, setPanoHatasi] = useState(false);
  const zamanlayici = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (zamanlayici.current !== null)
        window.clearTimeout(zamanlayici.current);
    },
    [],
  );

  const kopyala = async () => {
    try {
      await navigator.clipboard.writeText(deger);
      setKopyalandi(true);
      setPanoHatasi(false);
      if (zamanlayici.current !== null)
        window.clearTimeout(zamanlayici.current);
      zamanlayici.current = window.setTimeout(() => setKopyalandi(false), 2000);
    } catch {
      setPanoHatasi(true);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <code className="rakam min-w-0 break-all select-all font-mono text-lg font-semibold tracking-wider">
          {deger}
        </code>
        <IkonButon
          etiket={kopyalandi ? "Kopyalandı" : `${etiket} kopyala`}
          onClick={() => void kopyala()}
          className={kopyalandi ? "text-onay" : ""}
        >
          {kopyalandi ? <IkonTik /> : <IkonKopyala />}
        </IkonButon>
      </div>
      {/* Buton etiketi değişse de odak butonda kaldığı için ekran okuyucu
          duyurmaz; durum ayrıca bildirilir. */}
      <p role="status" aria-live="polite" className="gizli-erisilebilir">
        {kopyalandi ? `${etiket} panoya kopyalandı.` : ""}
      </p>
      {panoHatasi && (
        <p className="m-0 text-xs text-hata">
          Pano erişimi yok — değeri elle seçip kopyalayın.
        </p>
      )}
    </div>
  );
}
